import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react'
import * as api from '../../requests'
import { useHistory } from 'react-router'
import { ImageViewer as PDFViewer } from '@tecuity/pdf-viewer'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { REFRESH_PDF_LIST } from './ReportViewerReducer'
import { Helmet } from 'react-helmet'
import CaretLeft from '@tecuity/sos-icons/react/caret-left'
import CaretRight from '@tecuity/sos-icons/react/caret-right'
import CrossHeavy from '@tecuity/sos-icons/react/cross-heavy'
import PrintIcon from '@tecuity/sos-icons/react/print'
import PrintQueueIcon from '@tecuity/sos-icons/react/print-queue'
import './ReportViewer.scss'
import { ButtBox } from '@tecuity/granite-ui/ButtBox'
import { ContextButton } from '@tecuity/granite-ui/ContextButton'
import { setErrorToast, setSuccessToast } from '../../actions'
import FileDownloader from '../ImageViewer/FileDownloader'
import ReportEndpoint from './ReportEndpoint'
import { mergePdfByteArrays } from '../../Utilities/Utilities'
import { Spinner } from '../Loaders/Loaders'

let storageList = JSON.parse(localStorage.getItem('REPORTS_LIST')) || []

const ReportViewer = (props) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const [pdfList, setPdfList] = useState(storageList)
  const { refreshFlag } = useSelector((state) => state.reportViewer, shallowEqual)
  const { appUserRights } = useSelector((state) => state.auth, shallowEqual)
  const [sidebarExpanded, setSidebarExpanded] = useState(true)
  const [activePageIndex, setActivePageIndex] = useState(0)
  const [activeFilename, setActiveFilename] = useState(null)
  const [activeBytes, setActiveBytes] = useState([])
  const [reportsMap] = useState({})
  const [pages, setPages] = useState({})
  const [failedToLoadMap, setFailedToLoadMap] = useState({})
  const [downloadURL, setDownloadURL] = useState(null)
  const [downloadOnNextTick, setDownloadOnNextTick] = useState(false)
  const [showCloseConfirm, setShowCloseConfirm] = useState(false)
  const [endpointToFileHierarchy, setEndpointToFileHierarchy] = useState({})
  const [showLoadingSpinner, setShowLoadingSpinner] = useState(false)
  const [autoPrintEnabled, setAutoPrintEnabled] = useState(false)
  const [settledEndpoints, setSettledEndpoints] = useState({})
  const [endpointFailures, setEndpointFailures] = useState({})
  const downloader = useRef()
  const hasAutoPrinted = useRef(false)

  const markEndpointSettled = useCallback(
    (name, status) =>
      setSettledEndpoints((prev) => (prev[name] ? prev : { ...prev, [name]: status })),
    [],
  )

  const reportEndpointFailures = useCallback(
    (name, failures) => setEndpointFailures((prev) => ({ ...prev, [name]: failures })),
    [],
  )

  useEffect(() => {
    if (!appUserRights.includes('ViewImages')) {
      history.push('/')
    }
  }, [appUserRights])

  useEffect(() => {
    api.GET_APP_SETTING_BY_NAME(
      'AutomaticReportViewerPrintDialogue',
      (data) => {
        setAutoPrintEnabled(String(data.VALUE).toLowerCase() === 'true')
      },
      (e) => {
        console.error(e)
        setAutoPrintEnabled(false)
      },
    )
  }, [])

  useEffect(() => {
    if (refreshFlag) {
      setPdfList(JSON.parse(localStorage.getItem('REPORTS_LIST')) || [])
      dispatch({ type: REFRESH_PDF_LIST })
    }
  }, [refreshFlag])

  useEffect(() => {
    if (Object.entries(pages)[0] && !activeFilename) {
      selectFile({ filename: Object.keys(pages)[0] }, 0)
    }
  }, [pages])

  const selectFile = (pdfInfo, pageIndex = 0) => {
    setActivePageIndex(pageIndex)
    setActiveFilename(pdfInfo.filename)
  }

  const pageChanged = (pdfInfo, newPages) => {
    const pagesMap = { ...pages }
    pagesMap[pdfInfo.filename] = newPages
    setPages(pagesMap)
  }

  const clickSelectAll = (checked) => {
    const filenameKeys = Object.keys(pages)
    const pagesMap = {}
    filenameKeys.forEach((filename) => {
      const updatedPages = pages[filename].map((page) => {
        return { ...page, isChecked: checked }
      })
      pagesMap[filename] = updatedPages
    })
    setPages(pagesMap)
    const failedToLoadKeys = Object.keys(failedToLoadMap)
    failedToLoadKeys.forEach((key) => {
      failedToLoadMap[key].isChecked = checked
    })
  }

  const showPrintSelectedButtons = useMemo(() => {
    const filenameKeys = Object.keys(pages)
    let found = false
    filenameKeys.forEach((filename) => {
      if (pages[filename] && pages[filename].find((page) => page.isChecked)) found = true
    })
    const failedToLoadKeys = Object.keys(failedToLoadMap)
    failedToLoadKeys.forEach((key) => {
      if (failedToLoadMap[key].isChecked) found = false
    })
    return found
  }, [pages, failedToLoadMap])

  const showCloseSelectedButton = useMemo(() => {
    const filenameKeys = Object.keys(pages)
    let found = false
    filenameKeys.forEach((filename) => {
      if (pages[filename] && pages[filename].find((page) => page.isChecked)) found = true
    })
    const failedToLoadKeys = Object.keys(failedToLoadMap)
    failedToLoadKeys.forEach((key) => {
      if (failedToLoadMap[key].isChecked) found = true
    })
    return found
  }, [pages, failedToLoadMap])

  const isChecked = useMemo(() => {
    const filenameKeys = Object.keys(pages)
    let found = true
    filenameKeys.forEach((filename) => {
      if (pages[filename] && pages[filename].find((page) => !page.isChecked)) found = false
    })
    const failedToLoadKeys = Object.keys(failedToLoadMap)
    failedToLoadKeys.forEach((key) => {
      if (!failedToLoadMap[key].isChecked) found = false
    })
    return found
  }, [pages, failedToLoadMap])

  const handleSingleSendToPrintQueue = () => {
    const { info } = reportsMap[activeFilename]
    const reportInfo = {
      Description: info.Description,
      PrintQueueTypeID: info.PrintQueueTypeID,
      Xml: info.Xml,
      ReportName: info.ReportName,
      ImageNum: info.ImageNum || undefined,
    }
    api.ADD_TO_PRINT_QUEUE(
      reportInfo,
      (res) => {
        dispatch(
          setSuccessToast('Report successfully added to print queue. Please close the file.'),
        )
      },
      (err) => {
        dispatch(setErrorToast('Report NOT added to print queue.'))
      },
    )
  }

  const handleSendToPrintQueue = () => {
    const filenameKeys = Object.keys(pages)
    const promiseArray = []
    filenameKeys.forEach((filename) => {
      if (pages[filename].find((page) => page.isChecked)) {
        const { info } = reportsMap[filename]
        // console.log(info);
        const reportInfo = {
          Description: info.Description,
          PrintQueueTypeID: info.PrintQueueTypeID,
          Xml: info.Xml,
          ReportName: info.ReportName,
          ImageNum: info.ImageNum || undefined,
        }
        // add to promiseList
        // console.log(reportInfo)
        promiseArray.push(
          new Promise((resolve, reject) => {
            api.ADD_TO_PRINT_QUEUE(reportInfo, resolve, reject)
          }),
        )
      }
    })
    if (promiseArray.length > 0) {
      Promise.all(promiseArray)
        .then((reports) => {
          dispatch(setSuccessToast('Report(s) successfully added to print queue.'))
          closeSelectedReports()
        })
        .catch((err) => {
          dispatch(setErrorToast('Report(s) NOT added to print queue.'))
        })
    } else {
      dispatch(setErrorToast('No Reports Selected to send to Print Queue!'))
    }
  }

  const handleExport = () => {
    setDownloadOnNextTick(false)
    const blobObject = window.URL.createObjectURL(reportsMap[activeFilename].bytes)
    setDownloadURL(blobObject)
    setDownloadOnNextTick(true)
  }

  const getDataFromBlob = (myBlob) => {
    return new Promise((resolve, reject) => {
      let reader = new FileReader()
      reader.onload = () => {
        resolve(reader.result)
      }
      reader.onerror = reject
      reader.readAsArrayBuffer(myBlob)
    })
  }

  const handlePrint = (mergedBytes) => {
    const blobURL = window.URL.createObjectURL(
      mergedBytes
        ? new Blob([mergedBytes], { type: 'application/pdf' })
        : reportsMap[activeFilename].bytes,
    )
    const iframe = document.createElement('iframe') //load content in an iframe to print later
    document.body.appendChild(iframe)
    iframe.style.display = 'none'
    iframe.src = blobURL
    iframe.onload = function () {
      setTimeout(function () {
        iframe.focus()
        iframe.contentWindow.print()
        setShowLoadingSpinner(false)
      }, 1)
    }
  }

  const printableCheckedFileNames = useMemo(() => {
    const orderedFileNames = pdfList.flatMap(
      (pdfInfo) => endpointToFileHierarchy[pdfInfo.filename] || [],
    )
    return orderedFileNames.filter((filename) => {
      return pages[filename] && pages[filename].find((page) => page.isChecked)
    })
  }, [pdfList, endpointToFileHierarchy, pages])

  const mergePdfsForPrint = useCallback(async () => {
    try {
      setShowLoadingSpinner(true)
      const arrayOfBytes = []

      for (const fileName of printableCheckedFileNames) {
        const arrayBuffer = await reportsMap[fileName].bytes.arrayBuffer()
        arrayOfBytes.push(arrayBuffer)
      }
      return await mergePdfByteArrays(arrayOfBytes)
    } catch (err) {
      console.error('Error merging PDFs:', err)
      throw err
    }
  }, [printableCheckedFileNames, reportsMap])

  useEffect(() => {
    if (
      !autoPrintEnabled ||
      hasAutoPrinted.current ||
      pdfList.length === 0 ||
      !pdfList.every((pdfInfo) => settledEndpoints[pdfInfo.filename])
    ) {
      return
    }
    const allFailures = Object.values(endpointFailures).flat()
    if (allFailures.length > 0) {
      hasAutoPrinted.current = true
      const identifiers = allFailures
        .map((failure) => failure.ImageNum || failure.FilingNum || failure.Description)
        .join(', ')
      dispatch(
        setErrorToast(
          `Auto-print skipped: some reports could not be loaded (${identifiers}). Review the report list and print manually.`,
        ),
      )
      return
    }
    hasAutoPrinted.current = true
    const autoPrint = async () => {
      try {
        setShowLoadingSpinner(true)
        const orderedFileNames = pdfList.flatMap(
          (pdfInfo) => endpointToFileHierarchy[pdfInfo.filename] || [],
        )
        const arrayOfBytes = []
        for (const fileName of orderedFileNames.filter((filename) => reportsMap[filename])) {
          const arrayBuffer = await reportsMap[fileName].bytes.arrayBuffer()
          arrayOfBytes.push(arrayBuffer)
        }
        if (!arrayOfBytes.length) {
          setShowLoadingSpinner(false)
          dispatch(setErrorToast('No reports available to print.'))
          return
        }
        handlePrint(await mergePdfByteArrays(arrayOfBytes))
        dispatch(
          setSuccessToast(
            'The reports in the list will be cleared when the Report Viewer window is closed.',
          ),
        )
      } catch (err) {
        console.error('Error merging PDFs:', err)
        setShowLoadingSpinner(false)
      }
    }
    autoPrint()
  }, [autoPrintEnabled, pdfList, settledEndpoints, endpointFailures, endpointToFileHierarchy, reportsMap])

  useEffect(() => {
    if (!autoPrintEnabled) return undefined
    const clearReportsList = () => localStorage.setItem('REPORTS_LIST', JSON.stringify([]))
    window.addEventListener('pagehide', clearReportsList)
    return () => {
      window.removeEventListener('pagehide', clearReportsList)
      clearReportsList()
    }
  }, [autoPrintEnabled])

  React.useEffect(() => {
    if (downloadOnNextTick) {
      downloader.current.download()
    }
  }, [downloadOnNextTick])

  useEffect(async () => {
    if (reportsMap[activeFilename]) {
      try {
        let myImageData = await getDataFromBlob(reportsMap[activeFilename].bytes)
        setActiveBytes(myImageData)
      } catch (err) {
        console.error(err)
      }
    } else {
      setActiveBytes([])
    }
  }, [reportsMap, activeFilename])

  const closeSelectedReports = () => {
    // console.log(pages)
    // console.log(endpointToFileHierarchy)
    let tempStorageList = JSON.parse(localStorage.getItem('REPORTS_LIST')) || []
    let tempHierarchy = { ...endpointToFileHierarchy }
    const hierarchyKeys = Object.keys(endpointToFileHierarchy)
    const filenameKeys = Object.keys(pages)
    filenameKeys.forEach((filename) => {
      if ((pages[filename] || []).find((page) => page.isChecked)) {
        // console.log('selected filename:', filename);
        delete reportsMap[filename]
        delete pages[filename]
        //remove from hierarchy
        hierarchyKeys.forEach((endpointName) => {
          if ((tempHierarchy[endpointName] || []).includes(filename)) {
            // console.log('endpointName', endpointName)
            // console.log(tempHierarchy[endpointName])
            tempHierarchy[endpointName] = tempHierarchy[endpointName].filter(
              (file) => file !== filename,
            )
          }
        })
      }
    })
    hierarchyKeys.forEach((endpointName) => {
      if ((tempHierarchy[endpointName] || []).length === 0) {
        // console.log('removing from local storage', endpointName);
        tempStorageList = tempStorageList.filter((endpoint) => endpoint.filename !== endpointName)
        delete tempHierarchy[endpointName]
      }
    })
    setEndpointToFileHierarchy(tempHierarchy)
    let tempFailedToLoadMap = { ...failedToLoadMap }
    const failedToLoadKeys = Object.keys(tempFailedToLoadMap)
    failedToLoadKeys.forEach((key) => {
      if (tempFailedToLoadMap[key].isChecked) {
        tempStorageList = tempStorageList.filter((endpoint) => endpoint.filename !== key)
        tempFailedToLoadMap[key].IsClosed = true
      }
    })
    setFailedToLoadMap(tempFailedToLoadMap)
    // console.log(tempStorageList)
    localStorage.setItem('REPORTS_LIST', JSON.stringify(tempStorageList))
    setPdfList(tempStorageList)
    setActiveFilename(null)
    setActiveBytes([])
  }

  const selectFailedToLoad = (info, removeFromMap = false) => {
    setFailedToLoadMap((old) => {
      const temp = { ...old, [info.filename]: info }
      if (removeFromMap) delete temp[info.filename]
      return temp
    })
  }

  return (
    <div className="report-wrapper" data-sidebar-collapsed={!sidebarExpanded}>
      <Helmet>
        <style type="text/css">{`body{overflow: hidden;}`}</style>
      </Helmet>
      <nav
        className="report-sidebar"
        style={{ transform: !sidebarExpanded ? 'translateX(0px)' : '', marginTop: '35px' }}
      >
        <div className="sidebar-header">
          {'Reports '}
          <button
            className="collapse-envelope-button"
            title="Collapse Report"
            onClick={() => setSidebarExpanded(false)}
          >
            <CaretLeft />
          </button>
        </div>
        <div className="reports-actions">
          <ButtBox
            label={isChecked ? 'Deselect All' : 'Select All'}
            onChange={clickSelectAll}
            checked={isChecked}
          ></ButtBox>
        </div>
        <div className="reports-actions">
          <button
            title="Print Selected"
            onClick={async () => {
              try {
                handlePrint(await mergePdfsForPrint())
              } catch (err) {
                console.error(err)
                dispatch(setErrorToast('Failed to merge PDFs to print.'))
              }
            }}
            className="remove-button"
            disabled={!showPrintSelectedButtons}
          >
            <PrintIcon />
          </button>

          <button
            title="Send Selected to Print Queue"
            onClick={handleSendToPrintQueue}
            className="remove-button"
            disabled={!showPrintSelectedButtons}
          >
            <PrintQueueIcon />
          </button>

          <button
            onClick={() => {
              setShowCloseConfirm((show) => !show)
            }}
            title="Close Selected"
            className="remove-button"
            disabled={!showCloseSelectedButton}
          >
            <CrossHeavy />
          </button>
          {showLoadingSpinner && (
            <div style={{ margin: '20px' }}>
              <Spinner color="#7792d0" className="entity-step-loader" size="small" />
            </div>
          )}
        </div>

        {showCloseConfirm && (
          <div className="report-delete-confirm">
            <span>Are you sure you want to close the selected report(s)?</span>
            <div className="report-delete-confirm-actions">
              <ContextButton
                variant={'success'}
                onClick={() => {
                  setShowCloseConfirm(false)
                  closeSelectedReports()
                }}
              >
                Yes
              </ContextButton>
              <ContextButton
                variant={'danger'}
                onClick={() => {
                  setShowCloseConfirm(false)
                }}
              >
                No
              </ContextButton>
            </div>
          </div>
        )}
        {pdfList.map((pdfInfo, i) => {
          return (
            <ReportEndpoint
              pdfInfo={pdfInfo}
              key={`endpoint-${i}`}
              reportsMap={reportsMap}
              activeFilename={activeFilename}
              setActiveFilename={setActiveFilename}
              activePageIndex={activePageIndex}
              selectFile={selectFile}
              pageChanged={pageChanged}
              pages={pages}
              endpointToFileHierarchy={endpointToFileHierarchy}
              setEndpointToFileHierarchy={setEndpointToFileHierarchy}
              selectFailedToLoad={selectFailedToLoad}
              failedToLoadMap={failedToLoadMap}
              markEndpointSettled={markEndpointSettled}
              reportEndpointFailures={reportEndpointFailures}
            />
          )
        })}
        {(Object.keys(reportsMap).length === 0 || pdfList.length === 0) && (
          <h1>No Reports to Display...</h1>
        )}
      </nav>
      <div className="report-viewer-wrapper" data-envelope-collapsed={!sidebarExpanded}>
        <PDFViewer
          pdfBytes={activeBytes}
          title={activeFilename || 'Loading...'}
          onExport={handleExport}
          onPrint={handlePrint}
          onSendToPrintQueue={handleSingleSendToPrintQueue}
          thumbnailWidth={140}
          thumbnailWrapperChildren={
            <button className="expand-envelope-button" onClick={() => setSidebarExpanded(true)}>
              Expand Report
              <CaretRight />
            </button>
          }
          currentPageIndex={activePageIndex}
          onCurrentPageIndexChange={setActivePageIndex}
          imageViewerRights={{
            hasEditRights: appUserRights.includes('EditImages'),
            hasPrintRights: appUserRights.includes('PrintImages'),
            hasExportRights: appUserRights.includes('ExportImages'),
            hasPrintQueueRights: appUserRights.includes('PrintQueuePrintEntries'),
          }}
          hideEditButton={true}
          labelOverrides={{ print: 'PRINT CURRENT DOCUMENT' }}
        />
        <FileDownloader fileName={activeFilename} href={downloadURL} ref={downloader} />
      </div>
    </div>
  )
}

export default ReportViewer
